import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  BookOpen,
  ChartPie,
  ContactRound,
  GraduationCap,
  History,
  LayoutGrid,
  List,
  MonitorPlay,
  School,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { PERMISSIONS, canSeeMySpace } from "@utils/permissions";
import {
  SIDEBAR_MY_SPACE_DESCRIPTION_KEY,
  SIDEBAR_MY_SPACE_TITLE_KEY,
  SIDEBAR_TRAINING_OFFER_DESCRIPTION_KEY,
  SIDEBAR_TRAINING_OFFER_TITLE_KEY,
} from "@utils/utilsSidebarLabels";
import {
  getUsersGroupsDescriptionKey,
  getUsersGroupsReadPermissions,
  getUsersGroupsTitleKey,
} from "@utils/utilsUsersGroupsLabels";
import {
  canCreate,
  openCreatePicker,
} from "@components/CreateOptionPickerCst/host";
import {
  V2Route,
  V2RouteChild,
  V2RouteSection,
  V2RouteVisibilityContext,
} from "./types";

export const useNavigationRoutesV2 = (
  dataDetail: Record<string, any> | undefined,
  corporateId?: string,
): V2RouteSection[] => {
  const { t } = useTranslation("sidebar");
  const { user } = useAuth();

  const { dataDetail: campaigns } = useFetchDataDetail(
    dataDetail?.collect_campaigns && corporateId
      ? ServicesURL.findCampaignsAdmin.replace(":corporateId", corporateId)
      : null,
    PagesID.corporate,
  );

  const isProductOwner =
    !!user?.roles?.includes(RolesDigitedEnum.PRODUCT_OWNER) &&
    user?.organization_id === 1;

  // Titolo e descrizione della card utenti/gruppi dipendono dai permessi di
  // lettura: utenti, gruppi statici, gruppi dinamici.
  const usersGroupsReadPermissions = getUsersGroupsReadPermissions(
    user?.permissions,
  );

  const allSections: V2RouteSection[] = [
    {
      key: "menu",
      label: t("sections.menu") || "",
      type: "cards",
      items: [
        {
          kind: "card",
          key: "my-space",
          icon: React.createElement(LayoutGrid, { size: 18 }),
          title: t(SIDEBAR_MY_SPACE_TITLE_KEY) || "",
          description: t(SIDEBAR_MY_SPACE_DESCRIPTION_KEY) || "",
          to: "my-space",
          isVisible: ({ user: u }) => canSeeMySpace(u?.permissions),
        },
        {
          kind: "card",
          key: "catalog",
          title: t(SIDEBAR_TRAINING_OFFER_TITLE_KEY) || "",
          description: t(SIDEBAR_TRAINING_OFFER_DESCRIPTION_KEY) || "",
          icon: React.createElement(BookOpen, { size: 18 }),
          to: "catalog",
          section: "fe.corporate.catalog",
          // Come v1: senza learning_object.corporate.visibility la pagina
          // catalogo non mostra nessuna card (Percorsi, Corsi, Attività...),
          // quindi la card "Gestisci offerta formativa" non va mostrata.
          isVisible: ({ user: u }) =>
            !!u?.permissions?.includes(PERMISSIONS.catalog.corporateVisibility),
          createKind: "catalog",
        },
        {
          kind: "card",
          key: "users-groups",
          title: t(getUsersGroupsTitleKey(usersGroupsReadPermissions)) || "",
          description:
            t(getUsersGroupsDescriptionKey(usersGroupsReadPermissions)) || "",
          icon: React.createElement(ContactRound, { size: 18 }),
          to: "users-groups",
          // Visibile se l'utente può leggere almeno uno dei tab (utenti,
          // gruppi statici, gruppi dinamici).
          isVisible: ({ user: u }) =>
            [
              PERMISSIONS.users.read,
              PERMISSIONS.groups.static.read,
              PERMISSIONS.groups.dynamic.read,
            ].some((p) => !!u?.permissions?.includes(p)),
          createKind: "users-groups",
        },
        {
          kind: "card",
          key: "notifications",
          title: t("cards.notifications.title") || "",
          description: t("cards.notifications.description") || "",
          icon: React.createElement(Bell, { size: 18 }),
          legacyTo: "notifications",
          section: "corporate.notifications",
        },
        {
          kind: "card",
          key: "monitoring",
          title: t("cards.monitoring.title") || "",
          description: t("cards.monitoring.description") || "",
          icon: React.createElement(ChartPie, { size: 18 }),
          legacyTo: "dashboard",
          section: "smartconfigurator.quicksight.read",
        },
      ],
    },
    {
      key: "other-processes",
      label: t("sections.other-processes") || "",
      type: "cards",
      items: [
        {
          kind: "card",
          key: "physical-editions",
          title: t("items.physical-editions.title") || "",
          description: t("items.physical-editions.description") || "",
          icon: React.createElement(School, { size: 18 }),
          legacyTo: "physical-editions",
          section: "corporate.physical-editions",
        },
        {
          kind: "card",
          key: "virtual-editions",
          title: t("items.virtual-editions.title") || "",
          description: t("items.virtual-editions.description") || "",
          icon: React.createElement(MonitorPlay, { size: 18 }),
          legacyTo: "virtual-editions",
          section: "corporate.virtual-editions",
        },
        {
          kind: "card",
          key: "teachers-area",
          title: t("items.teachers-area.title") || "",
          description: t("items.teachers-area.description") || "",
          icon: React.createElement(GraduationCap, { size: 18 }),
          legacyTo: "teachers",
          section: "mediamanager.media.exercise.read",
        },
        {
          kind: "card",
          key: "other-processes",
          title: t("items.other-processes.title") || "",
          description: t("items.other-processes.description") || "",
          icon: React.createElement(List, { size: 18 }),
          children: [
            {
              key: "funded-training",
              title: t("children.funded-training") || "",
              legacyTo: "foundedTraining",
              section: "usermgmt.corporates.funded_training.write",
              requiresDataFlag: "enable_funded_training",
            },
            {
              key: "budget",
              title: t("children.budget") || "",
              legacyTo: "budget-management",
              section: "usermgmt.budget.read",
              // Visibile solo se l'utente vede almeno una card della pagina Gestione Budget
              isVisible: ({ user: u }) =>
                [
                  "budget-management.budget",
                  "usermgmt.budget.project.projects.view.section",
                  "usermgmt.budget.project.accounting.view.section",
                  "usermgmt.budget.setup.view.section",
                ].some((permission) => !!u?.permissions?.includes(permission)),
            },
            {
              key: "campaigns",
              title: t("children.campaigns") || "",
              legacyTo: "collect-campaigns",
              section: "usermgmt.corporates.collect_campaign.read",
              isVisible: ({ user: u, dataDetail: data, hasCampaigns }) =>
                !!data?.collect_campaigns &&
                (hasCampaigns ||
                  !!u?.permissions?.includes(
                    "usermgmt.corporates.collect_campaign.create",
                  )),
            },
            {
              key: "training-needs",
              title: t("children.training-needs") || "",
              legacyTo: "training-needs",
              section: "usermgmt.corporates.training_needs",
              requiresDataFlag: "enable_training_needs",
            },
          ],
        },
      ],
    },
    {
      key: "other-tools",
      label: t("sections.other-tools") || "",
      type: "simple",
      items: [
        {
          kind: "simpleItem",
          key: "configure-platform",
          title: t("items.configure-platform.title") || "",
          icon: React.createElement(Wrench, { size: 18 }),
          hasChevron: true,
          children: [
            {
              key: "initiatives",
              title: t("children.initiatives") || "",
              legacyTo: "initiative",
              section: "usermgmt.initiatives.create",
              routePermission: "corporate&&corporate.initiative",
            },
            {
              key: "attributes",
              title: t("children.attributes") || "",
              legacyTo: "attributes",
              section: "attributes",
            },
            {
              key: "skills",
              title: t("children.skills") || "",
              legacyTo: "skills",
              section: "skills.profession.retrieve",
            },
            {
              key: "survey",
              title: t("children.survey") || "",
              legacyTo: "survey",
              section: "learning_catalogue.learning_object.write",
              routePermission:
                "corporate&&corporate.initiative&&cms.wrapper.moderatedterms.read",
            },
            {
              key: "badge-certificates",
              title: t("children.badge-certificates") || "",
              legacyTo: "badge-certificates",
              section: "badge-certificates",
            },
            {
              key: "locations",
              title: t("children.locations") || "",
              legacyTo: "locations",
              section: "users.site.retrieve",
            },
            {
              key: "media-library",
              title: t("children.media-library") || "",
              legacyTo: "media-library",
              section: "corporate.mediaLibrary",
            },
            {
              key: "self-signup",
              title: t("children.self-signup") || "",
              legacyTo: "selfsignup",
              section: "selfsignup",
              isVisible: ({ dataDetail: data }) =>
                data?.self_signup === "LEARNER" ||
                data?.self_signup === "MANAGER",
            },
            {
              key: "brand-asset",
              title: t("children.brand-asset") || "",
              legacyTo: "brand-asset",
              section: "corporate.brandasset.read",
            },
            {
              key: "forbidden-terms",
              title: t("children.forbidden-terms") || "",
              legacyTo: "terms",
              section: "cms.wrapper.moderatedterms.read",
              routePermission:
                "corporate&&corporate.initiative&&cms.wrapper.moderatedterms.read",
            },
          ],
        },
        {
          kind: "simpleItem",
          key: "old-admin",
          title: t("items.old-admin.title") || "",
          icon: React.createElement(History, { size: 18 }),
          hasChevron: true,
          children: [
            {
              key: "users",
              title: t("children.users") || "",
              legacyTo: "users",
              section: "corporate.users",
            },
            {
              key: "custom-fields",
              title: t("children.custom-fields") || "",
              legacyTo: "users-custom-fields",
              section: "usermgmt.corporates.custom_fields.read",
              parentKey: "users",
            },
            {
              key: "static-groups",
              title: t("children.static-groups") || "",
              legacyTo: "groups",
              section: "corporate.groups",
            },
            {
              key: "dynamic-groups",
              title: t("children.dynamic-groups") || "",
              legacyTo: "groups-dynamic",
              section: "usermgmt.corporates.dynamic_groups.read",
            },
            {
              key: "catalog",
              title: t("children.catalog") || "",
              legacyTo: "catalog",
              section: "fe.corporate.catalog",
            },
          ],
        },
      ],
    },
    {
      key: "po-only",
      label: t("sections.po-only") || "",
      type: "simple",
      items: [
        {
          kind: "simpleItem",
          key: "product-management",
          title: t("items.product-management.title") || "",
          icon: React.createElement(ShieldCheck, { size: 18 }),
          hasChevron: false,
          legacyTo: "/",
          isVisible: () => isProductOwner,
        },
      ],
    },
  ];

  const visibilityContext: V2RouteVisibilityContext = {
    user,
    dataDetail,
    hasCampaigns: !!(campaigns?.output as object[] | undefined)?.length,
  };

  const isAllowed = (route: V2Route | V2RouteChild): boolean => {
    if (route.section && !user?.permissions?.includes(route.section)) {
      return false;
    }
    if (
      route.routePermission &&
      !route.routePermission
        .split("&&")
        .every((permission) => user?.permissions?.includes(permission))
    ) {
      return false;
    }
    if (route.requiresDataFlag && !dataDetail?.[route.requiresDataFlag]) {
      return false;
    }
    if (route.isVisible && !route.isVisible(visibilityContext)) {
      return false;
    }
    return true;
  };

  const withActionButton = (item: V2Route): V2Route => {
    if (!item.createKind) return item;
    const kind = item.createKind;
    // S05: il bottone Crea resta visibile e viene disabilitato se l'utente non
    // può creare nessun elemento di quel tipo.
    return {
      ...item,
      actionButton: {
        label: t("create-button") || "",
        onClick: () => openCreatePicker(kind),
        disabled: !canCreate(user, kind),
      },
    };
  };

  const resolveChildren = (
    children: V2RouteChild[] | undefined,
  ): V2RouteChild[] | undefined => {
    const allowed = children?.filter(isAllowed);
    return allowed?.map((child) => ({
      ...child,
      nested:
        !!child.parentKey &&
        allowed.some((sibling) => sibling.key === child.parentKey),
    }));
  };

  return allSections
    .map((section) => ({
      ...section,
      items: section.items
        .filter(isAllowed)
        .map((item) => ({
          ...withActionButton(item),
          children: resolveChildren(item.children),
        }))
        .filter((item) => !item.children || item.children.length > 0),
    }))
    .filter((section) => section.items.length > 0);
};

export * from "./types";
